<template>
  <LayoutDataTable
    :isLoading="showTable"
    :searchQuery="searchQuery"
    :perPage="perPage"
    :meta="dataMeta"
    :currentPage="currentPage"
    :totals="totals"
    :isShowTabs="true"
    :searchPlaceholder="$t('lbl_find_orders_by_order_code')"
    @setTab="handleTabClicked"
    @setPerPage="(value) => (perPage = value)"
    @setCurrentPage="(value) => (currentPage = value)"
    @setSearchQuery="(value) => (searchQuery = value)"
  >
    <template #default>
      <span class="ml-2">{{ $t('lbl_filter_orders_by_expected_delivery_date_') }}</span>
      <b-row class="w-full pl-2 pr-3 d-flex" style="margin-top: 5px;">
        <b-col>
          <b-row class="d-flex">
            <b-col style="min-width: 270px">
              <b-form-datepicker
                :locale="$i18n.locale"
                v-bind="$getCalendarLabel($i18n.locale)"
                id="start-date"
                :placeholder="$t('lbl_select_start_date')"
                v-model="fromDate"
                class="mb-2 mr-2"
                value-as-date
                today-button
                reset-button
              ></b-form-datepicker>
            </b-col>
            <span class="mb-2 font-weight-bold" style="height: 39px; line-height: 39px;">-</span>
            <b-col style="min-width: 270px">
              <b-form-datepicker
                :locale="$i18n.locale"
                v-bind="$getCalendarLabel($i18n.locale)"
                id="end-date"
                :placeholder="$t('lbl_select_end_date')"
                v-model="toDate"
                class="mb-2"
                value-as-date
                today-button
                reset-button
              ></b-form-datepicker>
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="4">
          <b-form-select
            class="ml-2 mr-1"
            placeholder
            v-model="statusFilter"
            :options="statusFilterOptions"
          />
        </b-col>
      </b-row>
      <b-row class="w-full pl-2 pr-3 d-flex">
        <b-col v-if="followMode === 'follow'">
          <b-button @click="handleClickBellIcon(null, true)" v-b-tooltip.top :title="selectedRows.length===0 ? 'You have not selected any order.' : ''" :disabled="selectedRows.length===0" size="sm" variant="outline-primary">Unfollow</b-button>
          <p class="d-inline-block mb-2 ml-2" style="font-size: 12px">Selected {{ selectedRows.length }} out of {{ perPage }} rows</p>
        </b-col>
        <b-col v-if="followMode === 'floating'">
          <b-button @click="handleClickBellIcon(null, false)" v-b-tooltip.top :title="selectedRows.length===0 ? 'You have not selected any order.' : ''" :disabled="selectedRows.length===0" size="sm" variant="primary" class="mr-1">Follow</b-button>
          <p class="d-inline-block mb-2 ml-2" style="font-size: 12px">Selected {{ selectedRows.length }} out of {{ perPage }} rows</p>
        </b-col>
      </b-row>
      <b-table
        ref="refOrdersTable"
        class="position-relative"
        :items="fetchOrdersList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        hover
        :empty-text="$t('lbl_no_matching_records_found')"
        :sort-desc.sync="isSortDirDesc"
        @row-clicked="handleViewProducts"
      >
        <template #cell(selecting)="scope">
          <span class="d-inline-block" style="width: 20px">
            <b-form-checkbox v-model="scope.item.isSelected" @change="(e) => checkRow(e, scope.item)" style="width: 20px;"></b-form-checkbox>
          </span>
        </template>
        <template #cell(order_code)="scope">
          <span class="text-nowrap">
            <div v-if="scope.item.order_read_flag === 1" class="fade-loading">
            </div>
            {{ scope.item.order_code }}</span>
        </template>
        <template #cell(customer)="scope">
          <span class="text-nowrap">{{ getCustomer(scope.item) }}</span>
        </template>
        <!-- <template #cell(address)="scope">
          <span class="text-nowrap">{{ getCustomerAddress(scope.item) }}</span>
        </template>
        <template #cell(phone)="scope">
          <span class="text-nowrap">{{ getCustomerPhone(scope.item) }}</span>
        </template> -->
        <template #cell(status)="scope">
          <div :set="(status = getStatusOrder(scope.item))">
            <b-badge :variant="status.color">{{ status.title }}</b-badge>
          </div>
        </template>
        <template #cell(avatar)="scope">
          <div class="d-flex flex-nowrap position-relative">
            <span v-for="(follower, index) in scope.item.followers" :key="follower.id"
              class="text-nowrap avatar position-absolute d-flex items-center justify-center"
              :style="{left: 20*index+'px', zIndex: index.toExponential, height: '38px'}"
            >
              <b-img
                v-if="follower.user.avatar"
                :src="appUtils.getImageURL(follower.user.avatar)"
                @error="$set(follower, index, { ...follower, avatar: false })"
                fluid
                alt=""
                rounded="circle"
                style="width: 40px; object-fit: contain;"
              ></b-img>
              <span
                v-else
                class="b-avatar badge-minimal badge-light-primary rounded-circle"
                style="width: 40px; background-color: transparent;"
              >
                <svg
                  viewBox="0 0 16 16"
                  width="1em"
                  height="1em"
                  focusable="false"
                  role="img"
                  aria-label="person fill"
                  aria-hidden="true"
                  alt="avatar"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  class="bi-person-fill b-icon bi"
                >
                  <g>
                    <path
                      fill-rule="evenodd"
                      d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                    ></path>
                  </g>
                </svg>
              </span>
            </span>
          </div>
        </template>
        <template #cell(order_date)="scope">
          <span class="text-nowrap">{{ formatDate(scope.item.assign_date, "HH:mm DD/MM/YYYY") }}</span>
        </template>
        <template #cell(return_date)="scope">
          <span class="text-nowrap">{{ formatDate(scope.item.return_date) }}</span>
        </template>
        <template #cell(action)="scope">
          <div class="text-right text-nowrap" @click.stop>
            <b-button
              :set="(isFollow = getIsFollow(scope.item.followers))"
              v-b-tooltip.hover.top
              :variant="isFollow ? 'outline-info' : 'outline-primary'"
              size="sm"
              :title="
                isFollow
                  ? $t('lbl_unfollow_order_s_notification')
                  : $t('lbl_follow_order_s_notification')
              "
              @click="handleClickBellIcon(scope.item.id, getIsFollow(scope.item.followers))"
            >
              <feather-icon
                size="16"
                :icon="isFollow ? 'BellOffIcon' : 'BellIcon'"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover.top
              variant="outline-primary ml-1"
              size="sm"
              :title="$t('lbl_view_detail')"
              @click="viewDetailOrders(scope.item)"
            >
              <feather-icon size="16" icon="EyeIcon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </template>
  </LayoutDataTable>
</template>

<script>
import { BTable, BButton, BTooltip, BModal } from "bootstrap-vue";
import { mapGetters, mapState } from "vuex";
import useOrdersList from "./useOrdersList";
import { ref } from "@vue/composition-api";
import LayoutDataTable from "../../layouts/table/LayoutDataTable.vue";
import appUtils from "@/utils/appUtils";
import OrdersDetail from "./Detail/index.vue";
import { ORDER_FORM_PROCESSOR_STATUS } from "@/utils/constant";

export default {
  name: "OrdersList",
  components: {
    LayoutDataTable,
    BTable,
    BButton,
    BTooltip,
    BModal,
    OrdersDetail
  },
  data() {
    return {
      appUtils,
      selectedRows: [],
      user: {}
    };
  },
  computed: {
    ...mapGetters("app", ["statusOptions"]),
    ...mapState({
      orders: state => state.orders
    })
  },
  async created() {
    this.user = appUtils.getLocalUser()
  },
  methods: {
    handleTabClicked(e) {
      switch (Number(e.target.ariaPosInSet)) {
        case 1:
          this.followMode = ""
          break;
        case 2:
          this.followMode = "follow"
          break;
        case 3:
          this.followMode = "floating"
          break;
        default:
          this.followMode = ""
          break;
      }
      this.selectedRows = []
    },
    checkRow(eventValue, row) {
      if (eventValue) {
        this.selectedRows.push(row)
        this.refOrdersTable.localItems[index].isSelected = true
      } else {
        this.selectedRows = this.selectedRows.filter(item => item.id !== row.id)
        this.refOrdersTable.localItems[index].isSelected = false
      }
    },
    getIsFollow(followers) {
      return followers?.map(f => f.user_id).includes(this.user.id) || false
    },
    async handleClickBellIcon(orderId, isFollow) {
      const param = {
        object_id: orderId ? [orderId] : this.selectedRows.map(row => row.id),
        type: 2
      }
      if (isFollow) {
        await this.$rf
          .getRequest("CommonRequest")
          .unfollowOrders(param)
      } else {
        await this.$rf
          .getRequest("CommonRequest")
          .followOrders(param)
      }
      this.refetchData();
    },
    getCustomer(row) {
      if (row.product_order.ContactOrder) {
        const customerSender = row.product_order.ContactOrder.find(
          x => x.type === 1
        );
        return customerSender?.name;
      } else {
        return "";
      }
    },
    getCustomerAddress(row) {
      if (row.product_order.ContactOrder) {
        const customerSender = row.product_order.ContactOrder.find(
          x => x.type === 1
        );
        return customerSender?.address1 || customerSender?.address2;
      } else {
        return "";
      }
    },
    getCustomerPhone(row) {
      if (row.product_order.ContactOrder) {
        const customerSender = row.product_order.ContactOrder.find(
          x => x.type === 1
        );
        return customerSender?.phone;
      } else {
        return "";
      }
    },
    getStatusOrder(row) {
      switch (row?.status) {
        case ORDER_FORM_PROCESSOR_STATUS.PENDING:
          return {
            title: this.$t("lbl_order_processor_pending"),
            color: "primary"
          };
        case ORDER_FORM_PROCESSOR_STATUS.PROCESSING:
          return {
            title: this.$t("lbl_order_processor_process"),
            color: "info"
          };
        case ORDER_FORM_PROCESSOR_STATUS.COMPLETE:
          return {
            title: this.$t("lbl_order_processor_complete"),
            color: "success"
          };
        case ORDER_FORM_PROCESSOR_STATUS.CANCEL:
        case ORDER_FORM_PROCESSOR_STATUS.SUPPLIER_CANCEL:
          return {
            title: this.$t("lbl_order_processor_cancel"),
            color: "danger"
          };

        default:
          return {
            title: this.$t("lbl_order_processor_pending"),
            color: "primary"
          };
      }
    },
    refreshTable() {
      this.showTable = true;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.refetchData();
        this.showTable = false;
      }, 2000);
    },
    formatDate(time, format = "DD/MM/YYYY") {
      if (!time) return
      const date = moment.utc(time).format();
      return moment
        .utc(date)
        .local()
        .format(format);
    },
    handleViewProducts(item) {
      const userData = appUtils.getLocalUser();

      if (userData?.processors?.length) {
        this.viewDetailOrders(item);
      }
    },
    deleteOrders(record) {
      if (!record?.order_id) {
        return;
      }

      const nodeEl = this.$createElement;
      const messageVNode = nodeEl("div", {
        domProps: {
          innerHTML: `Please confirm that you want to delete orders <strong>#${record.order_id}</strong>.`
        }
      });

      this.$bvModal
        .msgBoxConfirm([messageVNode], {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if (value) {
            // Code here...
            console.log(value);
          }
        })
        .catch(err => {
          // An error occurred
        });
    },
    viewDetailOrders(order) {
      this.$router
        .push({
          name: "detail-order-processor",
          params: { id: order.id }
        })
        .catch(_ => {});
    }
  },
  setup() {
    const dataTag = ref({});
    const {
      timeout,
      showTable,
      fetchOrdersList,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refOrdersTable,
      refetchData,
      allOpenRows,
      ORDERS_STATUS,
      getOrderStatus,
      recordDetail,
      showDetailOrder,
      statusFilter,
      statusFilterOptions,
      fromDate,
      toDate,
      followMode
    } = useOrdersList();

    return {
      timeout,
      showTable,
      dataTag,
      fetchOrdersList,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refOrdersTable,
      refetchData,
      allOpenRows,
      ORDERS_STATUS,
      getOrderStatus,
      recordDetail,
      showDetailOrder,
      statusFilter,
      statusFilterOptions,
      fromDate,
      toDate,
      followMode
    };
  }
};
</script>

<style lang="scss">
.avatar {
  min-width: 40px;
  max-width: 40px;
  height: 40px;
  top: -20px;
}
.b-table-details {
  background-color: #f6f6f9;
}
.fade-loading {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #2cccff;
  border-radius: 100rem;
  margin-right: -11px;
  transform: translate(-16px, -1px);
  position: relative;
}

.fade-loading:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: inherit;
  animation: fade 1s forwards infinite linear;
}

@keyframes fade {
  to {
    transform: scale(2);
    opacity: 0;
  }
}
</style>
